import type { RouterOutputs } from "@api/trpc/routers/_app";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTRPC } from "@/trpc/client";

export const useToggleSaveStudySet = ({ id }: { id: string }) => {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  return useMutation(
    trpc.studysets.save.mutationOptions({
      onMutate: async ({ isLiked }) => {
        await queryClient.cancelQueries({
          queryKey: trpc.studysets.getLiked.queryKey(),
        });
        await queryClient.cancelQueries({
          queryKey: trpc.studysets.getById.queryKey({ id }),
        });

        const previousStudySet = queryClient.getQueryData<
          RouterOutputs["studysets"]["getById"]
        >(trpc.studysets.getById.queryKey({ id }));

        const previousUserSets = queryClient.getQueryData<
          RouterOutputs["studysets"]["get"]
        >(trpc.studysets.get.queryKey());

        // Update study set detail
        queryClient.setQueryData<RouterOutputs["studysets"]["getById"]>(
          trpc.studysets.getById.queryKey({ id }),
          (old) => (old ? { ...old, isLiked: !isLiked } : old),
        );

        // Update user sets list
        queryClient.setQueryData<RouterOutputs["studysets"]["get"]>(
          trpc.studysets.get.queryKey(),
          (old) => {
            if (!old) return old;
            if (isLiked) {
              return old.filter((set) => set.id !== id);
            }
            const studySet = previousStudySet;
            if (studySet && !old.some((set) => set.id === id)) {
              return [
                ...old,
                {
                  ...studySet,
                  type: "liked",
                },
              ];
            }
            return old;
          },
        );

        return { previousUserSets, previousStudySet };
      },
      onError: (_err, _variables, context) => {
        if (context) {
          queryClient.setQueryData(
            trpc.studysets.get.queryKey(),
            context.previousUserSets,
          );
          queryClient.setQueryData(
            trpc.studysets.getById.queryKey({ id }),
            context.previousStudySet,
          );
        }
        toast.error("Failed to update study set like status");
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.studysets.get.queryKey(),
        });
        if (id) {
          queryClient.invalidateQueries({
            queryKey: trpc.studysets.getById.queryKey({ id }),
          });
        }
      },
      onSuccess: ({ message, isLiked }) => {
        if (!isLiked) {
          toast.success("Study Set removed from likes");
        } else {
          toast.success("Study set saved! 📚");
        }
      },
    }),
  );
};
